<template>
	<precoa-modal
		open
		:modal-title="modalTitle"
		:class="['sm:w-full sm:max-w-[384px]!']"
		@close="close"
	>
		<template #default>
			<div class="my-4 px-4 pb-4 text-center text-sm text-slate-500!">
				<p>{{ modalMessage }}</p>
				<p
					v-if="showOptionalEmailNote"
					class="mt-2 italic"
				>
					{{ t('document.everplansEmailOptionalNote') }}
				</p>
			</div>
			<div class="grid grid-cols-1 *:w-full *:max-w-none">
				<FormKit
					id="updateEmailfield"
					type="email"
					name="email"
					placeholder="Email"
					:validation="validation"
					:validation-rules="{ uniqueEverplansEmail }"
					:validation-messages="{
						required: t('document.everplansEmailRequired'),
						uniqueEverplansEmail: buildDuplicateEmailMessage,
					}"
					:validation-visibility="mode === 'everplans' ? 'live' : 'dirty'"
					decorator-class="border-gray-400! rounded-md h-6 mb-2"
					input-class="placeholder:text-slate-400!"
					v-model="email"
					@keydown.enter.prevent="saveEmail"
				/>
			</div>
		</template>
		<template #buttons>
			<button
				type="button"
				class="bg-accent-400! text-accent-700! hover:bg-accent-700! disabled:bg-secondary-300! disabled:text-secondary-400! disabled:hover:text-secondary-400! mb-3 w-full rounded-lg p-3 text-center font-bold hover:text-white! disabled:cursor-not-allowed!"
				:disabled="!emailIsValid"
				@click.prevent="saveEmail"
			>
				{{ t('general.save') }}
			</button>
		</template>
	</precoa-modal>
</template>

<script setup lang="ts">
import Plan from '@/classes/Plan'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import type { FormKitNode } from '@formkit/core'
import { FormKit, useFormKitNodeById } from '@formkit/vue'
import { ref, computed } from 'vue'

const emailField = useFormKitNodeById('updateEmailfield')
const emailIsValid = computed(
	() => emailField.value?.context?.state?.valid ?? false
)

const props = defineProps<{
	mode: 'policy' | 'everplans'
	planHolder: Plan
}>()

const { t } = usePresentationLocale()
const planStore = usePlanStore()
const email = ref(
	props.mode === 'policy'
		? props.planHolder.email
		: props.planHolder.everplansEmail
)

const emit = defineEmits<{
	(e: 'close'): void
	(e: 'saveEmail', email: string): void
}>()

const close = () => {
	emit('close')
}

const saveEmail = () => {
	if (!emailIsValid.value) {
		return
	}

	emit('saveEmail', email.value ?? '')
	close()
}

/**
 * A Premium Everplans always needs an email to enroll with, so it cannot be left blank.
 * A Free Everplans can be left blank and filled in later, so we only validate the format.
 */
const validation = computed(() => {
	if (props.mode === 'policy') {
		return 'email'
	}

	return props.planHolder.isEverplansPremium()
		? 'required|email|uniqueEverplansEmail'
		: 'email|uniqueEverplansEmail'
})

const showOptionalEmailNote = computed(
	() => props.mode === 'everplans' && !props.planHolder.isEverplansPremium()
)

// The other plan holders in this session whose Everplans account claims the given email
const planNamesClaiming = (candidateEmail: string) =>
	planStore
		.findPlansClaimingEverplansEmail(props.planHolder, candidateEmail)
		.map((plan) => plan.firstName)

const duplicateEmailPlanNames = computed(() =>
	planNamesClaiming(email.value ?? '')
)

/**
 * Everplans enrollment requires a distinct email per account, so an email another plan in
 * this session has already claimed cannot be reused here.
 *
 * Reads node.value rather than the bound ref because FormKit records which node properties
 * a rule touches and only re-runs it when one of them changes - a rule that never reads the
 * value is evaluated once and cached, so a duplicate typed after a unique email slips past.
 */
const uniqueEverplansEmail = (node: FormKitNode): boolean =>
	planNamesClaiming(String(node.value ?? '')).length === 0

const buildDuplicateEmailMessage = () => {
	if (duplicateEmailPlanNames.value.length === 0) {
		return ''
	}

	return t('document.everplansUniqueEmail', {
		names: duplicateEmailPlanNames.value.join(', '),
		name: props.planHolder.firstName,
	})
}

const modalTitle = computed(() => {
	return props.mode === 'policy'
		? t('document.policyEmailTitle')
		: t('document.everplansEmailTitle')
})

const modalMessage = computed(() => {
	return props.mode === 'policy'
		? t('document.policyEmailMessage')
		: t('document.everplansEmailMessage')
})
</script>
